/**
 * A2 — deployment and the two sign-offs, the end of the CRF lifecycle.
 *
 * ===========================================================================
 * TWO SIGN-OFFS, TWO KEYS, AND THEY ARE NOT THE SAME PERSON
 * ===========================================================================
 * `signoff.developer` and `signoff.project` are separate permissions held by
 * different roles — the seeded matrix gives Developer and Dev Head the first,
 * and Country Manager holds both. So they are two independent controls with
 * independent gates, never one "Sign off" button that does whichever the caller
 * happens to be allowed to do. A control whose MEANING depends on who is looking
 * at it is one nobody can be trained on.
 *
 * ===========================================================================
 * THE STATE IS READ FROM THE RECORD, BECAUSE THERE IS NOWHERE ELSE TO READ IT
 * ===========================================================================
 * `lifecycle.controller.ts` is five POSTs and no GET. Built against the write
 * routes alone this panel could offer two buttons and not say whether either had
 * already been pressed — the shape of the discount surface's OQ-8, and worse
 * here, because "has the developer signed?" is not a question anybody should
 * answer by pressing the button and seeing what happens. So `GET /work-orders/:id`
 * carries `signoffs` and `deployments`, and this panel renders them.
 *
 * ===========================================================================
 * THE PROJECT SIGN-OFF NAMES THE CUSTOMER'S WRITTEN ACCEPTANCE (Train F, F31)
 * ===========================================================================
 * Owner ruling R2: *"Project sign-off REQUIRES the customer's written acceptance.
 * RESTORE. That is the document that ends a dispute."* Since F30 the API refuses
 * `POST /crfs/:id/signoffs/project` unless the body names EXACTLY ONE of: a file
 * already attached to this CRF (`acceptance_attachment_id`, server-verified to
 * belong here and SNAPSHOTTED — name, byte size, sha256 — beside the FK), or fork
 * 5's counted escape hatch (`not_required: true` with a MANDATORY reason). This
 * panel is where the signer states that, in the SAME request that signs (fork 7).
 *
 * THIS PANEL DOES NOT PRE-EMPT THE SERVER. Nothing here disables the button when
 * no document is picked, and nothing here refuses a blank reason locally. The
 * server owns that rule and its wording, and this panel's promise — "signing
 * without a document is refused; the hatch needs a reason" — is proved by driving
 * this real form against the real API (`tests/signoff-acceptance.smoke.spec.ts`)
 * and reading the SERVER's refusal on screen. A local guard would make the proof
 * about the guard, and the day the two disagreed the operator would be told
 * something the server did not say. `workErrorText` passes 400/409 through
 * verbatim for exactly this reason.
 *
 * THE DOCUMENT COMES FROM THE CRF's OWN FILES (`GET /crfs/:id/files`) — the panel
 * names one; it does not upload one. The upload path exists (`CrfRecordPage`'s
 * Files tab: permission-gated, magic-byte checked) and a second uploader here
 * would be a second place for that gate to drift.
 *
 * WHAT WAS ACCEPTED IS RENDERED FROM THE SNAPSHOT (`acceptance_file_name`,
 * `acceptance_byte_size` on the sign-off row), never by looking the id up in the
 * files list. The files list is exactly the thing the snapshot is immune to.
 *
 * ===========================================================================
 * THE BACK CATALOGUE IS LABELLED, NOT PASSED OFF AS SIGNED (fork 21, F32)
 * ===========================================================================
 * Every project sign-off closed before migration 50 reads all-NULL on the seven
 * acceptance columns — the migration backfilled NULL on purpose, so those rows say
 * "nothing stated", not "acceptance was given". A green "Signed" pill with nothing
 * beside it would let that row pass for one closed against a document. So a SIGNED
 * PROJECT row that cites no document and states no waiver renders a label saying so
 * ("No acceptance on record …") — the same set the API's chase list serves
 * (`GET /countries/:cc/signoffs/acceptance-chase`). The label is PROJECT-only: a
 * DEVELOPER row is all-NULL by design (the acceptance is the second stage's, fork 1)
 * and still says nothing extra. Labelling here is READING; nothing on this panel
 * writes an acceptance onto an old row, and the only writer that can today
 * (re-signing) re-stamps the signature — see the record's chase-list e2e.
 *
 * ===========================================================================
 * `mail_status` IS A CLAIM ABOUT THE POST, NOT A MAIL THIS APP SENDS
 * ===========================================================================
 * Nothing here sends anything. The sign-off routes take `mail_status` as a fact
 * the operator RECORDS: the customer's acknowledgement arrived by email and
 * somebody is writing that down. The copy says exactly that, because a control
 * labelled "Send" that only wrote a column would be a lie about what the system
 * did — and this project's SMTP path is a deploy-time concern that is not wired
 * to this screen at all.
 *
 * `none` renders as "No mail recorded" rather than blank: an empty cell reads as
 * "not loaded yet".
 *
 * ===========================================================================
 * THE COVERAGE WARNING — OWNER RULING 2, ONE CLUSTER BEFORE THE REFUSAL
 * ===========================================================================
 * "Deploy REQUIRES every test point passed." The server does NOT yet refuse: measured
 * over HTTP on this branch, a work order whose only round is `in_test` with zero
 * verdicts is deployed with 201 and the `deployments` row is written. So this panel
 * WARNS and does not block, and the distinction is the whole design of this commit.
 *
 * A client that refused what the server accepts would be a second implementation of a
 * rule that does not exist yet — and the worse half of that trade is that the screen
 * becomes the authority. When the gate lands it must land in the API, where every
 * caller meets it; this warning exists so the refusal is not the first time anybody
 * hears about the missing verdicts.
 *
 * ⚠ IT IS SCOPED TO THE FIRST DEPLOY (owner fork 10). The deploy path deliberately
 * admits already-deployed work so redeploys can be recorded, and the gate is ruled to
 * apply to the first deploy only — refusing an emergency redeploy would arrive during
 * an incident, on the exact path being used to fix it. A warning shown on a path that
 * will never be refused is a warning operators learn to click past, so it is not
 * shown there. `deployed` is the same expression the button's own verb reads.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { Alert, Button, Checkbox } from '../../components/ui';
import type { CountryCode } from '../../domain/countries';
import { crfKeys, fetchCrfFiles } from '../crfs/api';
import type { CrfFileDto } from '../crfs/api';
import type { MailStatus, SignoffDto, SignoffType, WorkOrderRecordDto } from './api';
import { groupOutstanding, hasNoTestPoints } from './coverage';
import { isAcceptanceUnrecorded, useRecordDeployment, useSignOff } from './use-work';
import type { AcceptanceStatement } from './use-work';
import { formatBytes } from './WorkFilesPanel';
import { workErrorText } from './errors';
import type { ToastTone } from '../inquiries/toast';

const MAIL_LABEL: Readonly<Record<MailStatus, string>> = {
  none: 'No mail recorded',
  sent: 'Mail sent',
  received: 'Mail received',
  acknowledged: 'Acknowledged',
};

/** The three an operator can CHOOSE. `none` is the default, not a choice. */
const MAIL_CHOICES: readonly MailStatus[] = ['sent', 'received', 'acknowledged'];

/** The picker's empty choice — a value no attachment id can collide with. */
const NO_DOCUMENT = '';

/**
 * The success sentence names WHAT the row now says: the snapshotted file for a
 * document close, the hatch for a waiver — read from the RESPONSE, not from the
 * form, so it reports what the server recorded rather than what was asked for.
 */
function signedText(type: SignoffType, row: SignoffDto): string {
  const who = type === 'developer' ? 'Developer' : 'Project';
  const mail = MAIL_LABEL[row.mail_status].toLowerCase();
  if (row.acceptance_file_name !== null) {
    return `${who} sign-off recorded (${mail}) — accepted: ${row.acceptance_file_name}.`;
  }
  if (row.not_required === true) {
    return `${who} sign-off recorded (${mail}) — acceptance declared not required.`;
  }
  return `${who} sign-off recorded (${mail}).`;
}

/**
 * `deployments.mode` for the history row (migration 62). File-private, and the
 * register keeps its own copy — the house pattern the kind pill records: the rule
 * is shared (NULL is an em-dash, a stranger keeps the SERVER'S own word — never a
 * blank, never a guessed label), the component is not.
 */
const MODE_LABEL: Readonly<Record<string, string>> = { online: 'Online', offline: 'Offline' };
function modeLabel(mode: string | null): string {
  if (mode === null) return '—';
  return MODE_LABEL[mode] ?? mode;
}

export function LifecycleSummary({
  work,
  countryCode,
  canDeploy,
  canSignDeveloper,
  canSignProject,
  onToast,
}: {
  work: WorkOrderRecordDto;
  countryCode: CountryCode;
  canDeploy: boolean;
  canSignDeveloper: boolean;
  canSignProject: boolean;
  onToast: (text: string, tone: ToastTone) => void;
}): React.JSX.Element {
  const deploy = useRecordDeployment(countryCode);
  const sign = useSignOff(countryCode);
  /*
   * Migration 62 — mode and due date on the deploy write. `mode` starts NULL and there
   * is deliberately NO preselection: a preselected mode is the enum-default hazard
   * wearing a UI, an answer recorded for an operator who was never asked. Unpicked
   * stays unpicked, the keys stay off the wire, and the server stores NULL
   * ("unrecorded" — the honest value).
   */
  const [mode, setMode] = useState<'online' | 'offline' | null>(null);
  const [dueDate, setDueDate] = useState('');
  const [mailStatus, setMailStatus] = useState<MailStatus>('none');
  // The acceptance statement — three pieces of local state, resolved into ONE
  // statement at click time (`statement()` below). Kept separate so ticking the
  // hatch does not forget a picked document and vice versa; the server is told
  // whichever the hatch's tick says is in force.
  const [documentId, setDocumentId] = useState<string>(NO_DOCUMENT);
  const [notRequired, setNotRequired] = useState(false);
  const [reason, setReason] = useState('');

  const developer = work.signoffs.find((row) => row.type === 'developer') ?? null;
  const project = work.signoffs.find((row) => row.type === 'project') ?? null;
  const deployed = work.deployments.length > 0;

  // The documents the project signer can NAME. Only fetched for somebody who can
  // sign the project — a Developer's record page must not cost a files request it
  // cannot act on. `crfKeys.files` is the CRF feature's own key, so an upload on
  // the CRF's Files tab in another pane refreshes this picker too.
  const files = useQuery({
    queryKey: crfKeys.files(work.crf.id),
    queryFn: ({ signal }) => fetchCrfFiles(work.crf.id, signal),
    enabled: canSignProject,
    // FRESH AT THE MOMENT OF NAMING. The app default is a 30 s staleTime; a picker
    // that names the document a signature will cite must not offer a list from
    // half a minute ago — the acceptance is typically uploaded on the CRF's Files
    // tab (another pane, another person) moments before it is named here. Zero
    // means every mount and every window focus refetches (measured in the
    // pipeline smoke: with the default, a document attached after the panel
    // mounted was not in the list at sign time).
    staleTime: 0,
  });

  /** What the operator has STATED, resolved from the three fields. */
  const statement = (): AcceptanceStatement => {
    if (notRequired) return { kind: 'not_required', reason };
    if (documentId !== NO_DOCUMENT) return { kind: 'document', attachmentId: documentId };
    return { kind: 'unstated' };
  };

  const doSign = (type: SignoffType): void => {
    sign.mutate(
      {
        crfId: work.crf.id,
        type,
        mailStatus,
        // The developer route states nothing — `acceptanceBody` sends `{}` there
        // regardless, but the intent is spelt out here as well.
        acceptance: type === 'project' ? statement() : { kind: 'unstated' },
      },
      {
        onSuccess: (row) => onToast(signedText(type, row), 'success'),
        onError: (error) => onToast(workErrorText(error), 'error'),
      },
    );
  };

  return (
    <section
      aria-labelledby="lifecycle-heading"
      className="rounded-md border border-line bg-surface"
    >
      <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
        <h2 id="lifecycle-heading" className="text-body font-medium text-fg">
          Deployment &amp; sign-off
        </h2>
        <p className="text-label text-fg-muted">
          {deployed
            ? `${String(work.deployments.length)} ${
                work.deployments.length === 1 ? 'deployment' : 'deployments'
              }`
            : 'Not deployed'}
        </p>
      </header>

      <div className="flex flex-col gap-3 p-4">
        {work.deployments.length === 0 ? (
          <p className="text-body13 text-fg-muted">
            Nothing deployed yet. Deploying takes this work order off the board — the board
            is what is outstanding.
          </p>
        ) : (
          <ol className="flex flex-col gap-1">
            {work.deployments.map((row) => (
              <li
                key={row.id}
                data-testid={`deployment-${row.id}`}
                className="flex items-baseline gap-2 text-body13 text-fg"
              >
                <span className="font-medium capitalize">{row.kind}</span>
                {/* Migration 62: NULL is an em-dash in BOTH cells — never 'Online',
                  * never today's date. The back catalogue was never asked. */}
                <span data-testid="deployment-mode" className="text-label text-fg-muted">
                  {modeLabel(row.mode)}
                </span>
                <span data-testid="deployment-due" className="tnum text-label text-fg-muted">
                  {row.due_date === null ? '—' : `due ${row.due_date}`}
                </span>
                <span className="tnum text-label text-fg-muted">
                  {new Date(row.deployed_at).toLocaleDateString()}
                </span>
                {row.note === null ? null : <span className="text-fg-secondary">{row.note}</span>}
              </li>
            ))}
          </ol>
        )}

        {/* Ungated on purpose: D5 removes the ACTION, not the FACTS. Somebody
          * without `deploy.record` still benefits from knowing the work is not
          * covered — it is usually THEIR verdicts that are missing. */}
        <CoverageWarning coverage={work.coverage} alreadyDeployed={deployed} />

        {/* D5: without `deploy.record` the button is absent, not disabled — and the
          * mode/due controls with it: they are halves of the same ACTION. */}
        {canDeploy ? (
          <div className="flex flex-col gap-2">
            <fieldset className="flex flex-col gap-1">
              <legend className="text-label text-fg-muted">
                How this goes out. Unpicked is recorded as unrecorded — nothing is assumed.
              </legend>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-1.5 text-body13 text-fg">
                  <input
                    type="radio"
                    name="deploy-mode"
                    value="online"
                    checked={mode === 'online'}
                    onChange={() => setMode('online')}
                  />
                  Online
                </label>
                <label className="flex items-center gap-1.5 text-body13 text-fg">
                  <input
                    type="radio"
                    name="deploy-mode"
                    value="offline"
                    checked={mode === 'offline'}
                    onChange={() => setMode('offline')}
                  />
                  Offline
                </label>
              </div>
            </fieldset>
            <div className="flex items-center gap-2">
              <label htmlFor="deploy-due" className="text-label text-fg-muted">
                Due date
              </label>
              <input
                id="deploy-due"
                type="date"
                value={dueDate}
                onChange={(event) => setDueDate(event.target.value)}
                className="rounded-sm border border-line bg-surface px-2 py-1 text-body13 text-fg"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                disabled={deploy.isPending}
                onClick={() =>
                  deploy.mutate(
                    {
                      workOrderId: work.id,
                      kind: deployed ? 'redeploy' : 'deploy',
                      // Unstated stays ABSENT on the wire — the server stores NULL
                      // because the operator said nothing, not a null this client sent.
                      mode: mode ?? undefined,
                      due_date: dueDate === '' ? undefined : dueDate,
                    },
                    {
                      onSuccess: (row) =>
                        onToast(
                          `Recorded as ${row.kind}. This work order has left the board.`,
                          'success',
                        ),
                      onError: (error) => onToast(workErrorText(error), 'error'),
                    },
                  )
                }
              >
                {deploy.isPending
                  ? 'Recording…'
                  : deployed
                    ? 'Record a redeploy'
                    : 'Record deployment'}
              </Button>
            </div>
          </div>
        ) : null}

        <div className="flex flex-col gap-2 border-t border-line pt-3">
          <p className="text-label font-medium text-fg-muted">Sign-off</p>
          <SignoffRow label="Developer" row={developer} />
          <SignoffRow label="Project" row={project} />

          {canSignDeveloper || canSignProject ? (
            <>
              <label htmlFor="mail-status" className="mt-1 text-label text-fg-muted">
                How the customer&rsquo;s confirmation arrived, recorded with the sign-off.
                Nothing is emailed from here.
              </label>
              <select
                id="mail-status"
                value={mailStatus}
                onChange={(event) => setMailStatus(event.target.value as MailStatus)}
                className="w-fit rounded-sm border border-line bg-surface px-2 py-1 text-body13 text-fg"
              >
                <option value="none">{MAIL_LABEL.none}</option>
                {MAIL_CHOICES.map((status) => (
                  <option key={status} value={status}>
                    {MAIL_LABEL[status]}
                  </option>
                ))}
              </select>

              {/* The acceptance controls exist ONLY for a project signer: the
                * developer route refuses the fields, so a developer-only seat is
                * never offered a picker whose choice would turn its signature into
                * a 400. */}
              {canSignProject ? (
                <AcceptanceControls
                  countryCode={countryCode}
                  crfId={work.crf.id}
                  files={files.data ?? null}
                  filesError={files.isError ? workErrorText(files.error) : null}
                  documentId={documentId}
                  onDocumentId={setDocumentId}
                  notRequired={notRequired}
                  onNotRequired={setNotRequired}
                  reason={reason}
                  onReason={setReason}
                />
              ) : null}

              <div className="flex flex-wrap items-center gap-2">
                {canSignDeveloper ? (
                  <Button
                    variant="ghost"
                    disabled={sign.isPending}
                    onClick={() => doSign('developer')}
                  >
                    Sign as developer
                  </Button>
                ) : null}
                {canSignProject ? (
                  <Button disabled={sign.isPending} onClick={() => doSign('project')}>
                    Sign off the project
                  </Button>
                ) : null}
              </div>
            </>
          ) : (
            <p className="text-body13 text-fg-muted">Signing off is not part of your access.</p>
          )}

          {sign.isError ? (
            <Alert tone="danger" title="The sign-off was not recorded">
              {workErrorText(sign.error)}
            </Alert>
          ) : null}
          {deploy.isError ? (
            <Alert tone="danger" title="The deployment was not recorded">
              {workErrorText(deploy.error)}
            </Alert>
          ) : null}
        </div>
      </div>
    </section>
  );
}

/**
 * The customer's written acceptance — what the project signer STATES.
 *
 * A picker over the CRF's own files, and the hatch with its reason. The copy is
 * the PROMISE this panel makes about the server: no document and no reason → the
 * server refuses to close, and says so on screen. Nothing here enforces it; see
 * the file header for why that is deliberate.
 */
function AcceptanceControls({
  countryCode,
  crfId,
  files,
  filesError,
  documentId,
  onDocumentId,
  notRequired,
  onNotRequired,
  reason,
  onReason,
}: {
  countryCode: CountryCode;
  crfId: string;
  files: CrfFileDto[] | null;
  filesError: string | null;
  documentId: string;
  onDocumentId: (value: string) => void;
  notRequired: boolean;
  onNotRequired: (value: boolean) => void;
  reason: string;
  onReason: (value: string) => void;
}): React.JSX.Element {
  return (
    <fieldset
      className="mt-1 flex flex-col gap-2 rounded-sm border border-line p-3"
      data-testid="acceptance-controls"
    >
      <legend className="px-1 text-label font-medium text-fg">
        The customer&rsquo;s written acceptance
      </legend>
      <p className="text-body13 text-fg-muted">
        The project sign-off closes only against a document the customer sent — a file
        attached to this CRF — or a stated reason why none is required. Without one of
        those the server refuses to close it, and the refusal is shown here.
      </p>

      <label htmlFor="acceptance-document" className="text-label text-fg-muted">
        Accepted document
      </label>
      <select
        id="acceptance-document"
        value={documentId}
        disabled={notRequired}
        onChange={(event) => onDocumentId(event.target.value)}
        className="w-full max-w-md rounded-sm border border-line bg-surface px-2 py-1 text-body13 text-fg disabled:opacity-60"
      >
        <option value={NO_DOCUMENT}>— No document named —</option>
        {(files ?? []).map((file) => (
          <option key={file.id} value={file.id}>
            {file.file_name} ({formatBytes(file.size_bytes)})
          </option>
        ))}
      </select>
      {filesError !== null ? (
        <p className="text-label text-danger-fg">
          The CRF&rsquo;s files could not be listed: {filesError}
        </p>
      ) : files !== null && files.length === 0 ? (
        <p className="text-label text-fg-muted">
          Nothing is attached to this CRF yet. Upload the customer&rsquo;s acceptance on{' '}
          <Link
            to={`/countries/${countryCode}/crfs/${crfId}`}
            className="underline underline-offset-2 hover:text-fg"
          >
            the CRF&rsquo;s Files tab
          </Link>
          , then name it here.
        </p>
      ) : null}

      <Checkbox
        label="Acceptance not required for this sign-off"
        checked={notRequired}
        onChange={(event) => onNotRequired(event.target.checked)}
        data-testid="acceptance-not-required"
      />
      {notRequired ? (
        <>
          <label htmlFor="acceptance-reason" className="text-label text-fg-muted">
            Why no acceptance is required — required, and recorded with the sign-off
          </label>
          <textarea
            id="acceptance-reason"
            value={reason}
            onChange={(event) => onReason(event.target.value)}
            rows={2}
            maxLength={300}
            aria-required="true"
            placeholder="e.g. Internal rework of our own defect; no customer deliverable changed."
            className="w-full max-w-md rounded-sm border border-line bg-surface px-2 py-1 text-body13 text-fg"
          />
        </>
      ) : null}
    </fieldset>
  );
}

/**
 * What testing has NOT accounted for, before the deploy button — or nothing at all.
 *
 * ===========================================================================
 * THE ABSENT CASE IS THE ONE WORTH ASSERTING
 * ===========================================================================
 * Three of the four inputs render nothing: a covered grid, an already-deployed work
 * order (fork 10), and — necessarily — both at once. A test that only checks the
 * warning APPEARS would pass against a component that rendered it unconditionally,
 * which is a screen that cries wolf on every deploy in the system. `coverage.test.tsx`
 * drives the absent cases as first-class assertions.
 *
 * ===========================================================================
 * NUMBERS, NEVER A COUNT, AND GROUPED BY WHAT TO DO ABOUT THEM
 * ===========================================================================
 * "3 outstanding" tells nobody which requirements or which action. `groupOutstanding`
 * splits them by cause because the causes call for different work: record a verdict,
 * re-test against the wording that stands, or fix the code and test again.
 *
 * The two `covered: false` states are told apart by `points.length`, never by
 * `outstanding` being empty — an empty `outstanding` is BOTH "fully covered" and "no
 * grid at all" (owner fork 3), and those need opposite sentences.
 *
 * ===========================================================================
 * ⚠ RULE 5 — BOTH SENTENCES WERE AMENDED BECAUSE THE SERVER STOPPED AGREEING
 * ===========================================================================
 * This warning was written while the deploy gate was OFF, and it said so in terms:
 * *"Deploying is still recorded if you go ahead"*, and, on the no-grid branch, *"A
 * deployment recorded now says the work shipped and nothing about which requirements it
 * met"*. Both described a deployment that WOULD be recorded. `recordDeployment` then
 * gained the gate (owner ruling 2026-08-15, forks 3/9/10/13), and a first deployment
 * with an outstanding point is now refused **409 POINTS_OUTSTANDING** — measured against
 * a running server, not inferred: the screen offered `Record deployment` under this very
 * banner and the API answered *"test point 3 has no passing verdict"*.
 *
 * NEITHER SENTENCE MAY PROMISE THE REFUSAL UNCONDITIONALLY EITHER. Fork 13 grandfathers
 * every work order opened before `DEPLOY_GATE_CUTOVER`, and this component cannot see
 * `created_at` — it receives `coverage` and `alreadyDeployed` and nothing else. So the
 * copy states the rule and names the exemption rather than predicting one outcome. The
 * refusal itself carries `details.points`, which is why the sentence can promise that
 * the server names which.
 *
 * ⚠ THIS WAS NOT CAUGHT BY ANY TEST. `coverage.test.tsx` asserts the banner's PRESENCE,
 * its groups and its absent cases; the gate's e2e asserts the 409. Both were right about
 * their own half. The contradiction only existed BETWEEN them, and only a browser with
 * the real server behind it could read the promise and then press the button.
 */
function CoverageWarning({
  coverage,
  alreadyDeployed,
}: {
  coverage: WorkOrderRecordDto['coverage'];
  alreadyDeployed: boolean;
}): React.JSX.Element | null {
  if (coverage.covered || alreadyDeployed) return null;

  if (hasNoTestPoints(coverage)) {
    return (
      <Alert tone="warning" title="This CRF has no test points" className="mt-1">
        <p data-testid="coverage-warning-no-points">
          There is nothing for testing to have passed. Add the requirements to the
          CRF&rsquo;s grid and record a verdict for each &mdash; a first deployment is
          refused until there is a grid to have passed. Work opened before the gate came
          in is exempt, and the server says so when it answers.
        </p>
      </Alert>
    );
  }

  return (
    <Alert tone="warning" title="Testing has not accounted for every test point" className="mt-1">
      <ul data-testid="coverage-warning" className="flex flex-col gap-0.5">
        {groupOutstanding(coverage).map((group) => (
          <li key={group.gap} data-gap={group.gap}>
            {group.label}:{' '}
            <span className="tnum font-medium">
              {group.point_nos.map((n) => String(n)).join(', ')}
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-1">
        The verdicts are entered point by point under Testing. A first deployment is
        refused while any point is outstanding, and the refusal names which. Work opened
        before the gate came in is exempt.
      </p>
    </Alert>
  );
}

/**
 * One sign-off's state. `null` means no row exists — nobody has signed.
 *
 * On a signed PROJECT row the acceptance is read FROM THE SNAPSHOT on the row:
 * the file name and byte size the server recorded at sign time, or the hatch and
 * its reason.
 *
 * ⚠ RULE 5 (F32) — the all-null case SPLIT. F31 rendered nothing extra for EVERY
 * all-null row ("not stated is not no document"). That is still true of a DEVELOPER
 * row, which states no acceptance by design. A SIGNED PROJECT row that is all-null is
 * the back catalogue — closed before the acceptance requirement — and since F32 it is
 * LABELLED as such rather than left to look like a row closed against a document. The
 * label reads what the row says (no document, no stated reason) and what follows from
 * it (the acceptance is still to be chased); it does not claim to know why.
 */
function SignoffRow({
  label,
  row,
}: {
  label: string;
  row: WorkOrderRecordDto['signoffs'][number] | null;
}): React.JSX.Element {
  return (
    <p
      className="flex flex-wrap items-baseline gap-2 text-body13 text-fg"
      data-testid={`signoff-${label.toLowerCase()}`}
    >
      <span className="w-20 shrink-0 text-fg-muted">{label}</span>
      {row === null || row.status !== 'signed' ? (
        <span className="text-fg-muted">Not signed</span>
      ) : (
        <>
          <span className="rounded-pill bg-ok-bg px-2 py-0.5 text-label text-ok-fg">Signed</span>
          <span className="tnum text-label text-fg-muted">
            {row.signed_at === null ? '' : new Date(row.signed_at).toLocaleDateString()}
          </span>
          <span className="text-label text-fg-muted">{MAIL_LABEL[row.mail_status]}</span>
          {row.acceptance_file_name !== null ? (
            <span className="text-label text-fg-secondary" data-testid="signoff-acceptance">
              Accepted: {row.acceptance_file_name}
              {row.acceptance_byte_size === null
                ? ''
                : ` (${formatBytes(row.acceptance_byte_size)})`}
            </span>
          ) : row.not_required === true ? (
            <span className="text-label text-fg-secondary" data-testid="signoff-acceptance">
              Acceptance not required
              {row.not_required_reason === null ? '' : ` — ${row.not_required_reason}`}
            </span>
          ) : isAcceptanceUnrecorded(row) ? (
            <span
              className="rounded-sm bg-warn-bg px-1.5 py-0.5 text-label text-warn-fg"
              data-testid="signoff-acceptance"
            >
              No acceptance on record — signed with neither a document nor a stated reason;
              the customer&rsquo;s written acceptance is still to be chased.
            </span>
          ) : null}
        </>
      )}
    </p>
  );
}
